import { useAtom } from '@reatom/npm-react'
import { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Trans } from 'react-i18next'
import { useLatest } from 'react-use'
import styled from 'styled-components'
import { match, P } from 'ts-pattern'

import { AmountFormatted } from '@root/shared/core/format'
import { Snackbar } from '@root/shared/ui-kit/snackbar/snackbar'

import { SwapRequestModel } from '../../model/swap-request-model'

import { SnackbarTxHasContent } from './snackbar-tx-hash-content'

import type {
  SnackbarIntent,
  SnackbarProps,
} from '@root/shared/ui-kit/snackbar/snackbar'

type Props = {
  model: SwapRequestModel
  onClose?: () => void
}

export const SwapRequestSnackbar: React.FC<Props> = ({ model, onClose }) => {
  const [stage] = useAtom(model.stageAtom)
  const { t } = useTranslation()
  const network = model.request.pair.network
  const type = model.request.type
  const pair = model.request.pair

  const handleCloseRef = useLatest(onClose)

  const shouldScheduleClose = useMemo(
    () =>
      match(stage)
        .returnType<boolean>()
        .with(P.instanceOf(SwapRequestModel.StageGettingQuote), () => false)
        .with(P.instanceOf(SwapRequestModel.StageSending), () => false)
        .otherwise(() => true),
    [stage],
  )

  useEffect(() => {
    if (shouldScheduleClose) {
      setTimeout(() => {
        handleCloseRef.current?.()
      }, 5000)
    }
  }, [shouldScheduleClose, handleCloseRef])

  const i18nParams = useMemo(() => {
    return {
      ticker: pair.quote.ticker,
      tickerNative: pair.base.ticker,
    }
  }, [pair])

  const intent = useMemo(() => {
    return match(stage)
      .returnType<SnackbarIntent>()
      .with(P.instanceOf(SwapRequestModel.StageGettingQuote), () => 'info')
      .with(P.instanceOf(SwapRequestModel.StageSending), () => 'info')
      .with(P.instanceOf(SwapRequestModel.StageSent), () => 'info')
      .with(P.instanceOf(SwapRequestModel.StageConfirmed), () => 'success')
      .with(P.instanceOf(SwapRequestModel.StageFailed), () => 'error')
      .exhaustive()
  }, [stage])

  const title = useMemo(() => {
    return match(stage)
      .returnType<string>()
      .with(P.instanceOf(SwapRequestModel.StageConfirmed), () =>
        t('trading.swap.success-title'),
      )
      .with(P.instanceOf(SwapRequestModel.StageFailed), () =>
        t('trading.swap.error-title'),
      )
      .otherwise(() => t('trading.swap.processing-title'))
  }, [stage, t])

  const description = useMemo(() => {
    return match(stage)
      .with(P.instanceOf(SwapRequestModel.StageGettingQuote), () => (
        <Content>
          <ContentText>
            {t('trading.swap.getting-quote-description')}
          </ContentText>
        </Content>
      ))
      .with(P.instanceOf(SwapRequestModel.StageFailed), (stage) => (
        <Content>
          <ContentText>
            {type === 'buy'
              ? t('trading.buy.error-title', i18nParams)
              : t('trading.sell.error-title', i18nParams)}
          </ContentText>
          {stage.txHash ? (
            <SnackbarTxHasContent network={network} txHash={stage.txHash} />
          ) : undefined}
        </Content>
      ))
      .with(
        P.union(
          P.instanceOf(SwapRequestModel.StageSent),
          P.instanceOf(SwapRequestModel.StageSending),
        ),
        (stage) => (
          <Content>
            <ContentText>
              {type === 'buy' ? (
                <Trans
                  i18nKey="trading.buy.active-title"
                  components={{
                    amount: (
                      <AmountFormatted
                        amount={stage.quote.outputAmount}
                        currency={pair.quote.ticker}
                      />
                    ),
                  }}
                />
              ) : (
                <Trans
                  i18nKey="trading.sell.active-title"
                  components={{
                    amount: (
                      <AmountFormatted
                        amount={stage.quote.inputAmount}
                        currency={pair.quote.ticker}
                      />
                    ),
                  }}
                />
              )}
            </ContentText>
            {'txHash' in stage ? (
              <SnackbarTxHasContent network={network} txHash={stage.txHash} />
            ) : undefined}
          </Content>
        ),
      )
      .with(P.instanceOf(SwapRequestModel.StageConfirmed), (stage) => (
        <Content>
          <ContentText>
            {type === 'buy' ? (
              <Trans
                i18nKey="trading.buy.success-title"
                components={{
                  amount: (
                    <AmountFormatted
                      amount={stage.quote.outputAmount}
                      currency={pair.quote.ticker}
                    />
                  ),
                  amountNative: (
                    <AmountFormatted
                      amount={stage.quote.inputAmount}
                      currency={pair.base.ticker}
                    />
                  ),
                }}
              />
            ) : (
              <Trans
                i18nKey="trading.sell.success-title"
                components={{
                  amount: (
                    <AmountFormatted
                      amount={stage.quote.inputAmount}
                      currency={pair.quote.ticker}
                    />
                  ),
                  amountNative: (
                    <AmountFormatted
                      amount={stage.quote.outputAmount}
                      currency={pair.base.ticker}
                    />
                  ),
                }}
              />
            )}
          </ContentText>
          <SnackbarTxHasContent network={network} txHash={stage.txHash} />
        </Content>
      ))
      .exhaustive()
  }, [i18nParams, network, pair.base.ticker, pair.quote.ticker, stage, t, type])

  const icon = useMemo((): SnackbarProps['icon'] => {
    if (
      stage instanceof SwapRequestModel.StageGettingQuote ||
      stage instanceof SwapRequestModel.StageSending
    ) {
      return 'circular-loading'
    }
  }, [stage])

  return (
    <Snackbar
      icon={icon}
      onClose={onClose}
      description={description}
      intent={intent}
    >
      {title}
    </Snackbar>
  )
}

const Content = styled.div``
const ContentText = styled.div`
  font-size: 12px;
  color: var(--text-12);
  margin-bottom: 4px;
`
